import React, { useEffect, useState } from "react";
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from "../../../store/store";
import AXIOS from "../../../helpers/api";
import { BASE_API } from "../../../helpers/apiUrl";
import { capitalize, sliceString } from "../../../utils/string";
import { toast } from "../../../helpers/toast";
import { updateBatchPaymentAsync } from "../../../store/features/NP/supplier/supplierAPI";
import CustomModal from "../../../components/common/CustomModal";
import Dragger from "antd/es/upload/Dragger";
import { Button, Select } from "antd";
import FileIcon from "../../../assets/icons/FileIcon";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import useExcelDownload from "../../../hooks/useExcelDownload";
import { stringToArray } from "../../../utils/array";
import * as XLSX from "xlsx";
import { bulkCreatePriceMatrixAsync } from "../../../store/features/NP/payment/paymentAPI";
import {
  getAllAreaAsync,
  getAllRegionAsync,
} from "../../../store/features/NP/geoInfo/geoAPI";
import { ArrayOption, selectSearchOption } from "../../../helpers/siteConfig";

type Props = {
  open: boolean;
  handleClose: () => void;
  priceMatrixData: () => void;
  data?: any;
  closable?: boolean;
  getData?: () => void;
  allPriceMatrix?: any;
};

export default function ImportPriceMatrixExcelModal({
  open,
  handleClose,
  data,
  closable = false,
  getData,
  allPriceMatrix,
  priceMatrixData,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("No file chosen");
  const [regionId, setRegionId] = useState<string | undefined>(undefined);
  const [areaId, setAreaId] = useState(undefined);
  const [hasWarnings, setHasWarnings] = useState(false);
  const { excelLoading, downloadExcel, handleExcelLoading } =
    useExcelDownload("Price-Matrix-Excel");
  const [uploadLoading, setUploadLoading] = useState(false);
  const [fileList, setFileList] = useState<{ name: string; url: string }[]>([]);
  const [uploadedData, setUploadedData] = useState<any[]>([]);
  const dispatch = useAppDispatch();
  const { postLoading } = useAppSelector((state: RootState) => state.payment);
  const { regions, areas } = useAppSelector((state) => state.npGeo);

  useEffect(() => {
    dispatch(getAllRegionAsync({}));
  }, [dispatch]);

  useEffect(() => {
    if (regionId)
      dispatch(
        getAllAreaAsync({
          params: {
            region: regionId,
          },
        })
      );
  }, [dispatch, regionId]);

  // ✅ Helper for validating Amounts
  const validateAmount = (
    val: any,
    fieldName: string,
    row: number,
    markWarning: () => void
  ) => {
    if (val === undefined || val === null || val === "") return 0;

    const strVal = String(val).trim();

    // 1️⃣ No letters or special characters
    if (!/^-?\d+(\.\d+)?$/.test(strVal)) {
      toast(
        "warning",
        `Row ${row}: "${fieldName}" contains invalid value "${strVal}". "${fieldName}" can never contain letters or special characters.`
      );
      markWarning();
      return null;
    }

    const numVal = Number(strVal);

    // 2️⃣ Cannot be negative
    if (numVal < 0) {
      toast(
        "warning",
        `Row ${row}: "${fieldName}" has invalid value "${strVal}". "${fieldName}" cannot be negative.`
      );
      markWarning();
      return null;
    }

    // 3️⃣ Max 2 digits after decimal
    if (!/^\d+(\.\d{1,2})?$/.test(strVal)) {
      toast(
        "warning",
        `Row ${row}: "${fieldName}" has invalid value "${strVal}". "${fieldName}" must allow only up to 2 digits after the decimal point.`
      );
      markWarning();
      return null;
    }

    return numVal;
  };

  const handleUploadFile = (file: any) => {
    if (
      file?.name?.endsWith(".xls") ||
      file?.name?.endsWith(".xlsx") ||
      file?.type === "application/vnd.ms-excel" ||
      file?.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      setLoading(true);
      const reader = new FileReader();
      reader.readAsBinaryString(file);

      const fileNameArr = stringToArray(file?.name, ".");
      setFileName(
        `${sliceString(file?.name, 30, "...")}.${
          fileNameArr?.[fileNameArr?.length - 1]
        }`
      );

      reader.onload = (e) => {
        const data = e?.target?.result;
        const workBook = XLSX.read(data, {
          type: "binary",
          cellDates: true,
          dateNF: "mm/dd/yyyy",
        });
        const sheetName = workBook.SheetNames[0];
        const sheet = workBook.Sheets[sheetName];
        const parsedData = XLSX.utils.sheet_to_json(sheet, { raw: false });
        let warningsFound = false;

        const filteredData = parsedData.map((el: any, index: number) => {
          const markWarning = () => {
            warningsFound = true;
          };

          return {
            cluster:
              el?.["cluster"]?.trim()?.toLowerCase() ||
              el?.["Cluster Name"]?.trim()?.toLowerCase() ||
              "",
            zone:
              el?.["zone"]?.trim()?.toLowerCase() ||
              el?.["Zone Name"]?.trim()?.toLowerCase() ||
              "",
            // vendor: el?.["vendor"]?.trim() || el?.["Vendor"]?.trim() || "",
            vendorId:
              el?.["vendorId"]?.trim() || el?.["Vendor ID"]?.trim() || "",
            effectiveDate:
              el?.["effectiveDate"]?.trim() ||
              el?.["Effective Date"]?.trim() ||
              "",

            standardMissedCallLineItem:
              el?.["standardMissedCallLineItem"]?.trim() ||
              el?.["Standard_Missed Call Std_Line Item # 1"]?.trim() ||
              "",
            standardMissedCallAmount: validateAmount(
              el?.["standardMissedCallAmount"] ||
                el?.["Standard_Missed Call Std_Missed call Amount"],
              "standardMissedCallAmount",
              index + 1,
              markWarning
            ),
            standardValidTTSinglePGLineItem:
              el?.["standardValidTTSinglePGLineItem"]?.trim() ||
              el?.["Standard_Valid TT_Single PG Std_Line Item # 2"]?.trim() ||
              "",
            standardValidTTSinglePGAmount: validateAmount(
              el?.["standardValidTTSinglePGAmount"] ||
                el?.["Standard_Valid TT_Single PG Std_Valid TT Amount"],
              "standardValidTTSinglePGAmount",
              index + 1,
              markWarning
            ),

            standardValidTTDoublePGLineItem:
              el?.["standardValidTTDoublePGLineItem"]?.trim() ||
              el?.["Standard_Valid TT_Double PG Std_Line Item # 3"]?.trim() ||
              "",
            standardValidTTDoublePGAmount: validateAmount(
              el?.["standardValidTTDoublePGAmount"] ||
                el?.["Standard_Valid TT_Double PG Std_Valid TT Amount"],
              "standardValidTTDoublePGAmount",
              index + 1,
              markWarning
            ),
            standardPGRHLineItem:
              el?.["standardPGRHLineItem"]?.trim() ||
              el?.["Standard_PGRH Std_Line Item # 4"]?.trim() ||
              "",
            standardPGRHAmount: validateAmount(
              el?.["standardPGRHAmount"] ||
                el?.["Standard_PGRH Std_PGRH Amount"],
              "standardPGRHAmount",
              index + 1,
              markWarning
            ),

            premiumMissedCallLineItem:
              el?.["premiumMissedCallLineItem"]?.trim() ||
              el?.["Premium_Missed Call Prm_Line Item # 1"]?.trim() ||
              "",
            premiumMissedCallAmount: validateAmount(
              el?.["premiumMissedCallAmount"] ||
                el?.["Premium_Missed Call Prm_Missed call Amount"],
              "premiumMissedCallAmount",
              index + 1,
              markWarning
            ),
            premiumValidTTSinglePGLineItem:
              el?.["premiumValidTTSinglePGLineItem"]?.trim() ||
              el?.["Premium_Valid TT_Single PG Prm_Line Item # 2"]?.trim() ||
              "",
            premiumValidTTSinglePGAmount: validateAmount(
              el?.["premiumValidTTSinglePGAmount"] ||
                el?.["Premium_Valid TT_Single PG Prm_Valid TT Amount"],
              "premiumValidTTSinglePGAmount",
              index + 1,
              markWarning
            ),

            premiumValidTTDoublePGLineItem:
              el?.["premiumValidTTDoublePGLineItem"]?.trim() ||
              el?.["Premium_Valid TT_Double PG Prm_Line Item # 3"]?.trim() ||
              "",
            premiumValidTTDoublePGAmount: validateAmount(
              el?.["premiumValidTTDoublePGAmount"] ||
                el?.["Premium_Valid TT_Double PG Prm_Valid TT Amount"],
              "premiumValidTTDoublePGAmount",
              index + 1,
              markWarning
            ),
            premiumPGRHLineItem:
              el?.["premiumPGRHLineItem"]?.trim() ||
              el?.["Premium_PGRH Prm_Line Item # 4"]?.trim() ||
              "",
            premiumPGRHAmount: validateAmount(
              el?.["premiumPGRHAmount"] || el?.["Premium_PGRH Prm_PGRH Amount"],
              "premiumPGRHAmount",
              index + 1,
              markWarning
            ),

            contingencyMissedCallLineItem:
              el?.["contingencyMissedCallLineItem"]?.trim() ||
              el?.["Contingency_Missed Call Con_Line Item # 1"]?.trim() ||
              "",
            contingencyMissedCallAmount: validateAmount(
              el?.["contingencyMissedCallAmount"] ||
                el?.["Contingency_Missed Call Con_Missed call Amount"],
              "contingencyMissedCallAmount",
              index + 1,
              markWarning
            ),
            contingencyValidTTSinglePGLineItem:
              el?.["contingencyValidTTSinglePGLineItem"]?.trim() ||
              el?.[
                "Contingency_Valid TT_Single PG Con_Line Item # 2"
              ]?.trim() ||
              "",
            contingencyValidTTSinglePGAmount: validateAmount(
              el?.["contingencyValidTTSinglePGAmount"] ||
                el?.["Contingency_Valid TT_Single PG Con_Valid TT Amount"],
              "contingencyValidTTSinglePGAmount",
              index + 1,
              markWarning
            ),
            contingencyValidTTDoublePGLineItem:
              el?.["contingencyValidTTDoublePGLineItem"]?.trim() ||
              el?.[
                "Contingency_Valid TT_Double PG Con_Line Item # 3"
              ]?.trim() ||
              "",
            contingencyValidTTDoublePGAmount: validateAmount(
              el?.["contingencyValidTTDoublePGAmount"] ||
                el?.["Contingency_Valid TT_Double PG Con_Valid TT Amount"],
              "contingencyValidTTDoublePGAmount",
              index + 1,
              markWarning
            ),
            contingencyPGRHLineItem:
              el?.["contingencyPGRHLineItem"]?.trim() ||
              el?.["Contingency_PGRH Con_Line Item # 4"]?.trim() ||
              "",
            contingencyPGRHAmount: validateAmount(
              el?.["contingencyPGRHAmount"] ||
                el?.["Contingency_PGRH Con_PGRH Amount"],
              "contingencyPGRHAmount",
              index + 1,
              markWarning
            ),
          };
        });

        const validData = filteredData.filter((item) => item !== null);

        setUploadedData(validData);
        setHasWarnings(warningsFound);
        setLoading(false);
      };
    } else {
      setLoading(false);
      toast("error", "Please upload only excel file");
    }
  };

  const handleUploadExcel = () => {
    setUploadLoading(true);

    if (uploadedData.length > 0) {
      dispatch(
        bulkCreatePriceMatrixAsync({
          data: uploadedData,
          others: {
            reRender,
          },
        })
      );
      setUploadLoading(false);
    }

    // console.log("uploadedData:", uploadedData);
  };

  const handleDownloadExcel = () => {
    if (!regionId) {
      toast("warning", "Please select cluster");
      return;
    }
    handleExcelLoading(true);

    const selectedRegion = regions.find((r: any) => r._id === regionId);

    const excelData =
      areas && areas.length > 0
        ? areas.map((a: any) => ({
            "Cluster Name": selectedRegion?.regionName
              ? capitalize(selectedRegion.regionName)
              : "",
            "Zone Name": capitalize(a.areaName),
            // Vendor: "",
            "Vendor ID": "",
            "Effective Date": "",
            "Standard_Missed Call Std_Line Item # 1": "",
            "Standard_Missed Call Std_Missed call Amount": "",
            "Standard_Valid TT_Single PG Std_Line Item # 2": "",
            "Standard_Valid TT_Single PG Std_Valid TT Amount": "",
            "Standard_Valid TT_Double PG Std_Line Item # 3": "",
            "Standard_Valid TT_Double PG Std_Valid TT Amount": "",
            "Standard_PGRH Std_Line Item # 4": "",
            "Standard_PGRH Std_PGRH Amount": "",
            "Premium_Missed Call Prm_Line Item # 1": "",
            "Premium_Missed Call Prm_Missed call Amount": "",
            "Premium_Valid TT_Single PG Prm_Line Item # 2": "",
            "Premium_Valid TT_Single PG Prm_Valid TT Amount": "",
            "Premium_Valid TT_Double PG Prm_Line Item # 3": "",
            "Premium_Valid TT_Double PG Prm_Valid TT Amount": "",
            "Premium_PGRH Prm_Line Item # 4": "",
            "Premium_PGRH Prm_PGRH Amount": "",
            "Contingency_Missed Call Con_Line Item # 1": "",
            "Contingency_Missed Call Con_Missed call Amount": "",
            "Contingency_Valid TT_Single PG Con_Line Item # 2": "",
            "Contingency_Valid TT_Single PG Con_Valid TT Amount": "",
            "Contingency_Valid TT_Double PG Con_Line Item # 3": "",
            "Contingency_Valid TT_Double PG Con_Valid TT Amount": "",
            "Contingency_PGRH Con_Line Item # 4": "",
            "Contingency_PGRH Con_PGRH Amount": "",
          }))
        : [
            {
              "Cluster Name": selectedRegion?.regionName
                ? capitalize(selectedRegion.regionName)
                : "",
              "Zone Name": "",
              Vendor: "",
              "Vendor ID": "",
              "Effective Date": "",
              "Standard_Missed Call Std_Line Item # 1": "",
              "Standard_Missed Call Std_Missed call Amount": "",
              "Standard_Valid TT_Single PG Std_Line Item # 2": "",
              "Standard_Valid TT_Single PG Std_Valid TT Amount": "",
              "Standard_Valid TT_Double PG Std_Line Item # 3": "",
              "Standard_Valid TT_Double PG Std_Valid TT Amount": "",
              "Standard_PGRH Std_Line Item # 4": "",
              "Standard_PGRH Std_PGRH Amount": "",
              "Premium_Missed Call Prm_Line Item # 1": "",
              "Premium_Missed Call Prm_Missed call Amount": "",
              "Premium_Valid TT_Single PG Prm_Line Item # 2": "",
              "Premium_Valid TT_Single PG Prm_Valid TT Amount": "",
              "Premium_Valid TT_Double PG Prm_Line Item # 3": "",
              "Premium_Valid TT_Double PG Prm_Valid TT Amount": "",
              "Premium_PGRH Prm_Line Item # 4": "",
              "Premium_PGRH Prm_PGRH Amount": "",
              "Contingency_Missed Call Con_Line Item # 1": "",
              "Contingency_Missed Call Con_Missed call Amount": "",
              "Contingency_Valid TT_Single PG Con_Line Item # 2": "",
              "Contingency_Valid TT_Single PG Con_Valid TT Amount": "",
              "Contingency_Valid TT_Double PG Con_Line Item # 3": "",
              "Contingency_Valid TT_Double PG Con_Valid TT Amount": "",
              "Contingency_PGRH Con_Line Item # 4": "",
              "Contingency_PGRH Con_PGRH Amount": "",
            },
          ];

    downloadExcel(excelData);
    handleExcelLoading(false);
  };

  function reRender(val?: boolean) {
    if (val) {
      handleClose();
      setFileList([]);
      priceMatrixData();
    }
  }

  return (
    <>
      <CustomModal
        width={500}
        title=""
        open={open}
        handleClose={!uploadLoading && !postLoading ? handleClose : () => {}}
        closable={closable}
      >
        <div className="p-6">
          <h4 className="text-body text-lg font-medium leading-normal relative -top-[2.5px] mb-4">
            {`Upload the Price Matrix Sheet`}
          </h4>

          <div className="mb-3">
            <Select
              value={regionId}
              placeholder="Select cluster"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={ArrayOption(regions, "regionName", "_id", true)}
              onChange={(val) => {
                setRegionId(val);
                setAreaId(undefined);
              }}
              size="large"
              className="w-full"
            />
          </div>

          <Dragger
            name="file"
            action=""
            accept=".xlsx, .xls"
            maxCount={1}
            showUploadList={false}
            beforeUpload={() => false}
            onChange={(info) => handleUploadFile(info.file)}
            onDrop={(e) => handleUploadFile(e.dataTransfer.files[0])}
            disabled={loading || uploadLoading || postLoading}
            className="import_excel"
          >
            <Button
              className="flex justify-between items-center"
              size="large"
              disabled={loading || uploadLoading || postLoading}
              style={{ width: "100%" }}
            >
              <div className="flex items-center gap-2 text-sm font-normal font-roboto text-[#8797ae]">
                <span className="text-accent font-medium text-base leading-7 relative">
                  Choose File
                </span>
                <span className="border-r border-solid border-l-0 border-y-0 border-others-border h-4" />
                {loading ? "Uploading..." : fileName}
              </div>
            </Button>
          </Dragger>
          <div className="px-4 py-[9px] bg-[#fffbe6] rounded-md border border-[#ffe58f] text-sm text-[#874d00] mt-4">
            <span className="font-medium">Warning! </span>
            <span>
              Upload only xlsx file. For any blank column put N/A, otherwise
              data will be mismatch. Your can see sample file,{" "}
            </span>
            <button
              className="text-[#1890ff] cursor-pointer text-sm font-medium bg-transparent border-none"
              onClick={handleDownloadExcel}
              disabled={excelLoading}
            >
              {" "}
              click here{" "}
            </button>
          </div>

          <div className="mt-4 space-y-2">
            {fileList.map((file, index) => (
              <div
                key={index}
                className="flex justify-between items-center px-2 py-1 bg-slate-100 rounded-md"
              >
                <div className="flex items-center justify-start gap-3">
                  <FileIcon />
                  <div className="text-zinc-700 text-sm font-normal">
                    {file.name}
                  </div>
                </div>
                <div
                  className="cursor-pointer"
                  // onClick={() => handleDelete(index)}
                >
                  <DeleteIcon />
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center mt-7 gap-3">
            <Button
              onClick={() => handleClose()}
              size="large"
              className="w-52 cancel_btn !bg-[#8897AE] !text-white !border-[2px] !border-transparent hover:!border-[#8897AE] transition-all duration-200"
            >
              Cancel
            </Button>
            <Button
              size="large"
              type="primary"
              className="w-52"
              onClick={handleUploadExcel}
              disabled={
                postLoading || uploadedData?.length === 0 || hasWarnings
              }
              loading={postLoading}
            >
              Upload
            </Button>
          </div>
        </div>
      </CustomModal>
    </>
  );
}
